import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Activity, AlertTriangle, CheckCircle2, TrendingUp } from 'lucide-react'
import { api } from '@/lib/api/client'
import { formatNumber } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { CardSkeleton } from '@/components/common/skeleton'
import { ErrorState } from '@/components/common/error-state'

type Range = '24h' | '7d' | '30d'

export function OverviewPage() {
  const [range, setRange] = useState<Range>('7d')

  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: () => api.getHealth(),
    refetchInterval: 60_000,
  })

  const conversionsQuery = useQuery({
    queryKey: ['conversions', range],
    queryFn: () => api.getConversions({ range }),
  })

  const chartData = useMemo(() => {
    const buckets = conversionsQuery.data?.buckets ?? []
    const byDay = new Map<string, number>()
    for (const b of buckets) {
      byDay.set(b.day, (byDay.get(b.day) ?? 0) + b.count)
    }
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, count]) => ({ day, count }))
  }, [conversionsQuery.data])

  const totalConversions = useMemo(
    () => (conversionsQuery.data?.buckets ?? []).reduce((sum, b) => sum + b.count, 0),
    [conversionsQuery.data],
  )

  const totalDispatched = useMemo(
    () =>
      (conversionsQuery.data?.buckets ?? []).reduce(
        (sum, b) => sum + (b.dispatched ?? 0),
        0,
      ),
    [conversionsQuery.data],
  )

  const matchRate =
    totalConversions > 0 ? Math.round((totalDispatched / totalConversions) * 100) : null

  const dlqTotal = healthQuery.data
    ? Object.values(healthQuery.data.dlq).reduce((a, b) => a + b, 0)
    : null

  const lambdaErrors = healthQuery.data?.lambda_errors ?? {}

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Overview</h2>
          <p className="text-sm text-muted-foreground">
            Platform health and conversion metrics
          </p>
        </div>
        <Tabs
          tabs={[
            { id: '24h', label: '24h' },
            { id: '7d', label: '7d' },
            { id: '30d', label: '30d' },
          ]}
          activeTab={range}
          onTabChange={(id) => setRange(id as Range)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {conversionsQuery.isLoading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : conversionsQuery.isError ? (
          <div className="col-span-full">
            <ErrorState
              message={conversionsQuery.error.message}
              onRetry={() => conversionsQuery.refetch()}
            />
          </div>
        ) : (
          <>
            <KpiCard
              title="Total conversions"
              value={formatNumber(totalConversions)}
              icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
            />
            <KpiCard
              title="Dispatch rate"
              value={matchRate !== null ? `${matchRate}%` : '—'}
              subtitle={
                totalDispatched > 0
                  ? `${formatNumber(totalDispatched)} dispatched`
                  : 'No dispatch data'
              }
              icon={<Activity className="h-4 w-4 text-muted-foreground" />}
            />
            <KpiCard
              title="DLQ depth"
              value={
                healthQuery.isLoading
                  ? '…'
                  : dlqTotal !== null
                    ? formatNumber(dlqTotal)
                    : '—'
              }
              subtitle="matcher + dispatcher"
              icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
              badge={
                healthQuery.data && (
                  <Badge variant={healthQuery.data.healthy ? 'success' : 'warning'}>
                    {healthQuery.data.healthy ? 'Healthy' : 'Degraded'}
                  </Badge>
                )
              }
            />
            <KpiCard
              title="Lambda errors"
              value={
                healthQuery.isLoading
                  ? '…'
                  : formatNumber(
                      Object.values(lambdaErrors).reduce(
                        (sum, m) => sum + (m.errors ?? 0),
                        0,
                      ),
                    )
              }
              subtitle="last window"
              icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground" />}
            />
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conversions over time</CardTitle>
        </CardHeader>
        <CardContent>
          {conversionsQuery.isLoading ? (
            <div className="h-72 animate-pulse rounded bg-muted" />
          ) : conversionsQuery.isError ? (
            <ErrorState
              message={conversionsQuery.error.message}
              onRetry={() => conversionsQuery.refetch()}
            />
          ) : chartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No conversion data for this range.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(222 47% 20%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(222 47% 20%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(222 47% 20%)"
                  fill="url(#colorCount)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {healthQuery.data && (
        <Card>
          <CardHeader>
            <CardTitle>Lambda invocations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              {Object.entries(healthQuery.data.lambda_errors).map(([name, stats]) => (
                <div key={name} className="rounded-md border p-4">
                  <p className="font-medium capitalize">{name}</p>
                  <div className="mt-2 flex gap-6 text-sm text-muted-foreground">
                    <span>Errors: {stats.errors ?? 0}</span>
                    <span>Invocations: {stats.invocations ?? 0}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  badge,
}: {
  title: string
  value: string
  subtitle?: string
  icon: React.ReactNode
  badge?: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold">{value}</div>
          {badge}
        </div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  )
}
